import { useCallback, useEffect, useState } from "react";
import {
  CalendarDays,
  CalendarPlus,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ClipboardList,
  Clock3,
  ExternalLink,
  MapPin,
  RefreshCw,
  Save,
  Trash2,
  Users,
  X,
} from "lucide-react";

import {
  AdminActivityDetails,
  Assignment,
  CalendarActivity,
  Cohort,
  programApi,
} from "../api/program";
import MaterialDropzone from "../components/NewView/Common/MaterialDropzone";
import ProgramMaterials from "../components/NewView/Common/ProgramMaterials";
import AdminAssignmentsPage from "./AdminAssignmentsPage";

const initialActivity = {
  title: "",
  description: "",
  type: "offline" as CalendarActivity["type"],
  startsAt: "",
  endsAt: "",
  location: "",
  meetingUrl: "",
  consultationParticipantId: "",
  includeAdditionalParticipants: false,
  additionalParticipantIds: [] as string[],
};

const activityTypeLabels: Record<CalendarActivity["type"], string> = {
  offline: "Офлайн",
  online: "Онлайн",
  self_study: "Самостійна робота",
  community: "Спільнота",
  consultation: "Консультація",
};

const activityStatusLabels: Record<CalendarActivity["status"], string> = {
  scheduled: "Заплановано",
  completed: "Завершено",
  cancelled: "Скасовано",
};

const DATETIME_STEP_SECONDS = 5 * 60;

const toDateTimeInput = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return offsetDate.toISOString().slice(0, 16);
};

const toLocalDateTimeInput = (date: Date) => {
  const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return offsetDate.toISOString().slice(0, 16);
};

const getDefaultActivityTimes = () => {
  const startsAt = new Date();
  startsAt.setMinutes(0, 0, 0);

  const endsAt = new Date(startsAt);
  endsAt.setHours(endsAt.getHours() + 1);

  return {
    startsAt: toLocalDateTimeInput(startsAt),
    endsAt: toLocalDateTimeInput(endsAt),
  };
};

const activityParticipantNames = (activity: CalendarActivity) =>
  (activity.participantIds || [])
    .map((participant) =>
      typeof participant === "string" ? "" : participant.username
    )
    .filter(Boolean);

const AdminCalendarPage = () => {
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [cohortId, setCohortId] = useState("");
  const [calendarCohortId, setCalendarCohortId] = useState("all");
  const [activities, setActivities] = useState<CalendarActivity[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [calendarMonth, setCalendarMonth] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  );
  const [calendarCompact, setCalendarCompact] = useState(false);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(new Date());
  const [form, setForm] = useState(initialActivity);
  const [createModalMode, setCreateModalMode] = useState<
    "activity" | "consultation" | null
  >(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [activeTab, setActiveTab] = useState<"activities" | "assignments">(
    "activities"
  );
  const [assignmentCreateSignal, setAssignmentCreateSignal] = useState(0);
  const [selectedActivityDetails, setSelectedActivityDetails] =
    useState<AdminActivityDetails | null>(null);
  const [activityDetailsLoading, setActivityDetailsLoading] = useState(false);
  const [activityDetailsSaving, setActivityDetailsSaving] = useState(false);
  const [attendanceSaving, setAttendanceSaving] = useState(false);
  const [selectedPresentIds, setSelectedPresentIds] = useState<Set<string>>(
    new Set()
  );
  const [activityEditForm, setActivityEditForm] = useState({
    title: "",
    description: "",
    type: "offline" as CalendarActivity["type"],
    status: "scheduled" as CalendarActivity["status"],
    startsAt: "",
    endsAt: "",
    location: "",
    meetingUrl: "",
  });
  const [createMaterialFiles, setCreateMaterialFiles] = useState<File[]>([]);
  const [detailsMaterialFiles, setDetailsMaterialFiles] = useState<File[]>([]);
  const [materialsSaving, setMaterialsSaving] = useState(false);

  const loadCohorts = async () => {
    try {
      const data = await programApi.listCohorts();
      setCohorts(data);
      setCohortId((current) => current || data[0]?._id || "");
    } catch (error: any) {
      setMessage(error.response?.data?.message || "Не вдалося отримати потоки.");
    } finally {
      setLoading(false);
    }
  };

  const loadCalendar = useCallback(async () => {
    if (!cohorts.length) {
      setActivities([]);
      setAssignments([]);
      return;
    }
    try {
      const selectedCohorts =
        calendarCohortId === "all"
          ? cohorts
          : cohorts.filter((cohort) => cohort._id === calendarCohortId);
      const calendars = await Promise.all(
        selectedCohorts.map((cohort) =>
          programApi.getCohortCalendar(cohort._id)
        )
      );
      setActivities(calendars.flatMap((calendar) => calendar.activities));
      setAssignments(calendars.flatMap((calendar) => calendar.assignments));
    } catch (error: any) {
      setMessage(
        error.response?.data?.message || "Не вдалося отримати календар."
      );
    }
  }, [calendarCohortId, cohorts]);

  useEffect(() => {
    loadCohorts();
  }, []);

  useEffect(() => {
    loadCalendar();
  }, [loadCalendar]);

  const openCreateModal = (mode: "activity" | "consultation") => {
    const defaultTimes = getDefaultActivityTimes();
    setForm({
      ...initialActivity,
      ...defaultTimes,
      type:
        mode === "consultation"
          ? ("consultation" as CalendarActivity["type"])
          : ("offline" as CalendarActivity["type"]),
    });
    setCreateModalMode(mode);
  };

  const closeCreateModal = () => {
    setCreateModalMode(null);
    setForm(initialActivity);
    setCreateMaterialFiles([]);
  };

  const createActivity = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!cohortId) {
      setMessage("Спочатку оберіть потік.");
      return;
    }

    const participantIds =
      form.type === "consultation"
        ? Array.from(
            new Set([
              form.consultationParticipantId,
              ...(form.includeAdditionalParticipants
                ? form.additionalParticipantIds
                : []),
            ].filter(Boolean))
          )
        : [];

    if (form.type === "consultation" && participantIds.length === 0) {
      setMessage("Для консультації потрібно обрати хоча б одного учасника.");
      return;
    }

    try {
      setSaving(true);
      const createdActivity = await programApi.createActivity({
        cohortId,
        title: form.title.trim(),
        description: form.description.trim(),
        type: form.type,
        audience: form.type === "consultation" ? "selected" : "cohort",
        participantIds,
        startsAt: new Date(form.startsAt).toISOString(),
        endsAt: new Date(form.endsAt).toISOString(),
        location: form.location.trim(),
        meetingUrl: form.meetingUrl.trim(),
      });
      if (createMaterialFiles.length) {
        await programApi.addActivityMaterials(createdActivity._id, {
          files: createMaterialFiles,
        });
      }
      setForm(initialActivity);
      setCreateMaterialFiles([]);
      setCreateModalMode(null);
      setMessage(
        form.type === "consultation"
          ? "Консультацію додано до календаря."
          : "Активність додано до календаря."
      );
      await loadCalendar();
    } catch (error: any) {
      setMessage(
        error.response?.data?.message || "Не вдалося створити активність."
      );
    } finally {
      setSaving(false);
    }
  };

  const removeActivity = async (id: string) => {
    if (!window.confirm("Видалити цю активність?")) return;
    try {
      await programApi.deleteActivity(id);
      await loadCalendar();
    } catch (error: any) {
      setMessage(
        error.response?.data?.message || "Не вдалося видалити активність."
      );
    }
  };

  const openActivityDetails = async (id: string) => {
    const fallbackActivity = activities.find((activity) => activity._id === id);
    const fallbackCohort = fallbackActivity
      ? cohortById.get(String(fallbackActivity.cohortId)) || null
      : null;

    if (fallbackActivity) {
      const fallbackDetails: AdminActivityDetails = {
        activity: fallbackActivity,
        cohort: fallbackCohort,
        participants:
          fallbackCohort?.participantIds?.map((participant) => ({
            user: participant,
            present: false,
            markedAt: null,
            markedBy: null,
          })) || [],
      };

      setSelectedActivityDetails(fallbackDetails);
      setSelectedPresentIds(new Set());
      setActivityEditForm({
        title: fallbackActivity.title,
        description: fallbackActivity.description || "",
        type: fallbackActivity.type,
        status: fallbackActivity.status,
        startsAt: toDateTimeInput(fallbackActivity.startsAt),
        endsAt: toDateTimeInput(fallbackActivity.endsAt),
        location: fallbackActivity.location || "",
        meetingUrl: fallbackActivity.meetingUrl || "",
      });
    }

    try {
      setActivityDetailsLoading(true);
      const details = await programApi.getActivityDetails(id);
      setSelectedActivityDetails(details);
      setSelectedPresentIds(
        new Set(
          details.participants
            .filter((participant) => participant.present)
            .map((participant) => participant.user._id)
        )
      );
      setActivityEditForm({
        title: details.activity.title,
        description: details.activity.description || "",
        type: details.activity.type,
        status: details.activity.status,
        startsAt: toDateTimeInput(details.activity.startsAt),
        endsAt: toDateTimeInput(details.activity.endsAt),
        location: details.activity.location || "",
        meetingUrl: details.activity.meetingUrl || "",
      });
    } catch (error: any) {
      setMessage(
        error.response?.data?.message || "Не вдалося відкрити активність."
      );
    } finally {
      setActivityDetailsLoading(false);
    }
  };

  const closeActivityDetails = () => {
    setSelectedActivityDetails(null);
    setSelectedPresentIds(new Set());
    setDetailsMaterialFiles([]);
  };

  const saveActivityDetails = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedActivityDetails) return;

    try {
      setActivityDetailsSaving(true);
      await programApi.updateActivity(selectedActivityDetails.activity._id, {
        title: activityEditForm.title.trim(),
        description: activityEditForm.description.trim(),
        type: activityEditForm.type,
        status: activityEditForm.status,
        startsAt: new Date(activityEditForm.startsAt).toISOString(),
        endsAt: new Date(activityEditForm.endsAt).toISOString(),
        location: activityEditForm.location.trim(),
        meetingUrl: activityEditForm.meetingUrl.trim(),
      });
      const details = await programApi.getActivityDetails(
        selectedActivityDetails.activity._id
      );
      setSelectedActivityDetails(details);
      setMessage("Активність оновлено.");
      await loadCalendar();
    } catch (error: any) {
      setMessage(
        error.response?.data?.message || "Не вдалося оновити активність."
      );
    } finally {
      setActivityDetailsSaving(false);
    }
  };

  const toggleParticipantAttendance = (userId: string) => {
    setSelectedPresentIds((current) => {
      const next = new Set(current);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const saveAttendance = async () => {
    if (!selectedActivityDetails) return;

    try {
      setAttendanceSaving(true);
      const details = await programApi.updateActivityAttendance(
        selectedActivityDetails.activity._id,
        Array.from(selectedPresentIds)
      );
      setSelectedActivityDetails(details);
      setSelectedPresentIds(
        new Set(
          details.participants
            .filter((participant) => participant.present)
            .map((participant) => participant.user._id)
        )
      );
      setMessage("Відвідуваність збережено.");
      await loadCalendar();
    } catch (error: any) {
      setMessage(
        error.response?.data?.message || "Не вдалося зберегти відвідуваність."
      );
    } finally {
      setAttendanceSaving(false);
    }
  };

  const addDetailsMaterials = async () => {
    if (!selectedActivityDetails) return;

    if (!detailsMaterialFiles.length) {
      setMessage("Додай файл матеріалу.");
      return;
    }

    try {
      setMaterialsSaving(true);
      await programApi.addActivityMaterials(selectedActivityDetails.activity._id, {
        files: detailsMaterialFiles,
      });
      const details = await programApi.getActivityDetails(
        selectedActivityDetails.activity._id
      );
      setSelectedActivityDetails(details);
      setDetailsMaterialFiles([]);
      setMessage("Матеріали додано.");
      await loadCalendar();
    } catch (error: any) {
      setMessage(
        error.response?.data?.message || "Не вдалося додати матеріали."
      );
    } finally {
      setMaterialsSaving(false);
    }
  };

  const deleteDetailsMaterial = async (materialId: string) => {
    if (!selectedActivityDetails) return;

    try {
      setMaterialsSaving(true);
      await programApi.deleteActivityMaterial(
        selectedActivityDetails.activity._id,
        materialId
      );
      const details = await programApi.getActivityDetails(
        selectedActivityDetails.activity._id
      );
      setSelectedActivityDetails(details);
      setMessage("Матеріал видалено.");
      await loadCalendar();
    } catch (error: any) {
      setMessage(
        error.response?.data?.message || "Не вдалося видалити матеріал."
      );
    } finally {
      setMaterialsSaving(false);
    }
  };

  const cohortById = new Map(cohorts.map((cohort) => [cohort._id, cohort]));
  const selectedFormCohort = cohortById.get(cohortId);
  const selectedFormParticipants = selectedFormCohort?.participantIds || [];
  const monthStart = new Date(
    calendarMonth.getFullYear(),
    calendarMonth.getMonth(),
    1
  );
  const mondayOffset = (monthStart.getDay() + 6) % 7;
  const daysInMonth = new Date(
    calendarMonth.getFullYear(),
    calendarMonth.getMonth() + 1,
    0
  ).getDate();
  const calendarCells = [
    ...Array.from({ length: mondayOffset }, () => null),
    ...Array.from({ length: daysInMonth }, (_, index) => index + 1),
  ];
  const monthDays = Array.from(
    { length: daysInMonth },
    (_, index) =>
      new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), index + 1)
  );
  const dateKey = (value: string | Date) => {
    const date = new Date(value);
    return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
  };
  const entriesByDate = new Map<
    string,
    Array<{
      id: string;
      kind: "activity" | "assignment";
      title: string;
      cohortId: string;
      time: string;
    }>
  >();

  activities.forEach((activity) => {
    const key = dateKey(activity.startsAt);
    entriesByDate.set(key, [
      ...(entriesByDate.get(key) || []),
      {
        id: activity._id,
        kind: "activity",
        title: activity.title,
        cohortId: String(activity.cohortId),
        time: new Date(activity.startsAt).toLocaleTimeString("uk-UA", {
          hour: "2-digit",
          minute: "2-digit",
        }),
      },
    ]);
  });
  assignments.forEach((assignment) => {
    const key = dateKey(assignment.dueAt);
    entriesByDate.set(key, [
      ...(entriesByDate.get(key) || []),
      {
        id: assignment._id,
        kind: "assignment",
        title: assignment.title,
        cohortId:
          typeof assignment.cohortId === "string"
            ? assignment.cohortId
            : assignment.cohortId._id,
        time: "Дедлайн",
      },
    ]);
  });
  const selectedDayEntries =
    selectedCalendarDate.getFullYear() === calendarMonth.getFullYear() &&
    selectedCalendarDate.getMonth() === calendarMonth.getMonth()
      ? entriesByDate.get(dateKey(selectedCalendarDate)) || []
      : [];
  const changeCalendarMonth = (delta: number) => {
    const nextMonth = new Date(
      calendarMonth.getFullYear(),
      calendarMonth.getMonth() + delta,
      1
    );
    setCalendarMonth(nextMonth);
    setSelectedCalendarDate(nextMonth);
  };

  return (
    <section className="min-w-0 overflow-x-hidden px-1 py-4 sm:px-6 sm:py-7 lg:px-8">
      <div className="mx-auto min-w-0 max-w-7xl">
        <div className="mb-4 grid gap-3 sm:mb-5 sm:grid-cols-[1fr_auto] sm:items-end">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.14em] text-[#8a6f00] sm:text-sm">
              Кабінет адміністратора
            </p>
            <h2 className="mt-1 text-[30px] font-black leading-[0.98] tracking-[-0.04em] text-black sm:text-3xl">
              Календар потоків
            </h2>
          </div>
          <div className="grid w-full gap-2 sm:w-auto sm:grid-cols-2 lg:grid-cols-4">
            <button
              type="button"
              onClick={() => openCreateModal("activity")}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-2xl border-2 border-black bg-[#ffd400] px-4 text-sm font-black text-black shadow-[3px_3px_0_#000] transition hover:-translate-y-0.5 sm:h-11"
            >
              <CalendarPlus size={16} />
              + Активність
            </button>
            <button
              type="button"
              onClick={() => openCreateModal("consultation")}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-2xl border-2 border-black bg-white px-4 text-sm font-black text-black shadow-[3px_3px_0_#ffd400] transition hover:-translate-y-0.5 sm:h-11"
            >
              <Users size={16} />
              + Консультація
            </button>
            <button
              type="button"
              onClick={() => {
                setActiveTab("assignments");
                setAssignmentCreateSignal((current) => current + 1);
              }}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-2xl border-2 border-black bg-[#ffd400] px-4 text-sm font-black text-black shadow-[3px_3px_0_#000] transition hover:-translate-y-0.5 sm:h-11"
            >
              <ClipboardList size={16} />
              + Завдання
            </button>
            <button
              type="button"
              onClick={() => loadCalendar()}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-2xl border-2 border-black/10 bg-white px-4 text-sm font-black text-black transition hover:bg-[#fff8cf] sm:h-11"
            >
              <RefreshCw size={16} />
              Оновити
            </button>
          </div>
        </div>

        <div className="mb-5 grid grid-cols-2 gap-2 rounded-2xl border border-[#E3DED6] bg-white p-2 shadow-sm sm:mb-6">
          <button
            type="button"
            onClick={() => setActiveTab("activities")}
            className={`inline-flex min-h-12 flex-1 items-center justify-center gap-1.5 rounded-xl px-2 text-center text-xs font-black leading-tight transition sm:min-h-11 sm:gap-2 sm:px-4 sm:text-sm ${
              activeTab === "activities"
                ? "border-2 border-black bg-[#ffd400] text-black shadow-[4px_4px_0_#000]"
                : "text-[#625C55] hover:bg-[#F7F4EF]"
            }`}
          >
            <CalendarPlus className="hidden sm:block" size={17} />
            <span>
              Активності <br className="sm:hidden" />
              календаря
            </span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("assignments")}
            className={`inline-flex min-h-12 flex-1 items-center justify-center gap-1.5 rounded-xl px-2 text-center text-xs font-black leading-tight transition sm:min-h-11 sm:gap-2 sm:px-4 sm:text-sm ${
              activeTab === "assignments"
                ? "border-2 border-black bg-[#ffd400] text-black shadow-[4px_4px_0_#000]"
                : "text-[#625C55] hover:bg-[#F7F4EF]"
            }`}
          >
            <ClipboardList className="hidden sm:block" size={17} />
            <span>
              Завдання <br className="sm:hidden" />
              по тижнях
            </span>
          </button>
        </div>

        {message && (
          <div className="mb-5 rounded-lg border border-[#E5D2C5] bg-[#FFF8F3] px-4 py-3 text-sm text-[#8A4A2E]">
            {message}
          </div>
        )}

        {activeTab === "assignments" ? (
          <AdminAssignmentsPage
            embedded
            createSignal={assignmentCreateSignal}
            onAssignmentCreated={loadCalendar}
          />
        ) : (
        <>
        <div className="mb-6 overflow-hidden rounded-2xl border border-[#E3DED6] bg-white shadow-sm">
          <div className="grid gap-3 border-b border-[#EEEAE4] p-4 sm:p-5 lg:grid-cols-[1fr_auto] lg:items-center">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <CalendarDays size={18} className="h-[18px] w-[18px] shrink-0 text-[#8a6f00] sm:h-5 sm:w-5" />
                <h3 className="min-w-0 font-bold leading-tight text-[#312D28]">
                  Загальний календар
                </h3>
              </div>
              <p className="mt-1 max-w-xl text-sm leading-5 text-[#817A72]">
                Активності та дедлайни завдань усіх потоків.
              </p>
            </div>
            <div className="grid min-w-0 gap-2 sm:grid-cols-[18rem_auto]">
              <select
                value={calendarCohortId}
                onChange={(event) => setCalendarCohortId(event.target.value)}
                className="h-11 w-full min-w-0 rounded-lg border border-[#DCD7CF] bg-white px-3 text-sm font-semibold text-[#4E4943]"
              >
                <option value="all">Усі потоки</option>
                {cohorts.map((cohort) => (
                  <option key={cohort._id} value={cohort._id}>
                    {cohort.name} · {cohort.code}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => setCalendarCompact((current) => !current)}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border-2 border-black bg-white px-4 text-sm font-black text-black shadow-[3px_3px_0_#ffd400] transition hover:-translate-y-0.5"
              >
                {calendarCompact ? (
                  <ChevronDown size={17} />
                ) : (
                  <ChevronUp size={17} />
                )}
                {calendarCompact ? "Розгорнути" : "Компактно"}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 border-b border-[#EEEAE4] px-3 py-3 sm:px-5">
            <button
              type="button"
              onClick={() => changeCalendarMonth(-1)}
              className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-[#E3DED6] text-[#625C55] hover:bg-[#F7F4EF]"
              aria-label="Попередній місяць"
            >
              <ChevronLeft size={18} />
            </button>
            <h4 className="min-w-0 text-center text-sm font-bold capitalize text-[#312D28] sm:text-base">
              {calendarMonth.toLocaleDateString("uk-UA", {
                month: "long",
                year: "numeric",
              })}
            </h4>
            <button
              type="button"
              onClick={() => changeCalendarMonth(1)}
              className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-[#E3DED6] text-[#625C55] hover:bg-[#F7F4EF]"
              aria-label="Наступний місяць"
            >
              <ChevronRight size={18} />
            </button>
          </div>

          {calendarCompact ? (
            <div className="w-full overflow-hidden">
              <div className="-mx-px overflow-x-auto px-3 py-4 sm:px-5">
                <div className="flex min-w-max gap-2">
                  {monthDays.map((dayDate) => {
                    const key = dateKey(dayDate);
                    const entries = entriesByDate.get(key) || [];
                    const selected = key === dateKey(selectedCalendarDate);
                    const isToday = key === dateKey(new Date());

                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setSelectedCalendarDate(dayDate)}
                        className={`relative w-[76px] shrink-0 rounded-2xl border px-2 py-3 text-center transition ${
                          selected
                            ? "border-black bg-[#fff8cf] shadow-[4px_4px_0_#ffd400]"
                            : "border-[#EEEAE4] bg-white hover:border-black/30"
                        }`}
                      >
                        <p className="text-[10px] font-black uppercase text-[#8A8178]">
                          {dayDate
                            .toLocaleDateString("uk-UA", { weekday: "short" })
                            .replace(".", "")}
                        </p>
                        <div
                          className={`mx-auto mt-2 grid h-10 w-10 place-items-center rounded-xl text-lg font-black ${
                            selected
                              ? "border-2 border-black bg-[#ffd400] text-black"
                              : isToday
                              ? "bg-black text-[#ffd400]"
                              : "bg-[#F7F4EF] text-black"
                          }`}
                        >
                          {dayDate.getDate()}
                        </div>
                        <div className="mt-2 flex h-2 items-center justify-center gap-1">
                          {Array.from({ length: Math.min(entries.length, 3) }).map(
                            (_, index) => (
                              <span
                                key={index}
                                className={`h-1.5 w-1.5 rounded-full ${
                                  entries[index]?.kind === "assignment"
                                    ? "bg-black"
                                    : "bg-[#ffd400] ring-1 ring-black/20"
                                }`}
                              />
                            )
                          )}
                          {entries.length > 3 && (
                            <span className="text-[9px] font-black text-black">
                              +{entries.length - 3}
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="border-t border-[#EEEAE4] p-4 sm:p-5">
                <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <p className="text-xs font-black uppercase tracking-[0.12em] text-[#8a6f00]">
                      Обраний день
                    </p>
                    <h4 className="mt-1 text-xl font-black capitalize tracking-[-0.03em] text-black">
                      {selectedCalendarDate.toLocaleDateString("uk-UA", {
                        weekday: "long",
                        day: "numeric",
                        month: "long",
                      })}
                    </h4>
                  </div>
                  <p className="text-sm font-bold text-[#817A72]">
                    {selectedDayEntries.length
                      ? `${selectedDayEntries.length} подій`
                      : "Без подій"}
                  </p>
                </div>

                {selectedDayEntries.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-[#D8D2C9] bg-[#FAF9F7] px-4 py-6 text-center text-sm font-semibold text-[#817A72]">
                    На цей день поки нічого не заплановано.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {selectedDayEntries.map((entry) => {
                      const cohort = cohortById.get(entry.cohortId);

                      return (
                        <button
                          key={`${entry.kind}-${entry.id}`}
                          type="button"
                          onClick={() => {
                            if (entry.kind === "activity") {
                              openActivityDetails(entry.id);
                            }
                          }}
                          className={`flex w-full min-w-0 items-center justify-between gap-3 rounded-2xl border p-3 text-left transition hover:-translate-y-0.5 ${
                            entry.kind === "assignment"
                              ? "border-black/10 bg-[#fff8cf]"
                              : "border-[#E3DED6] bg-white hover:border-black hover:shadow-[3px_3px_0_#ffd400]"
                          }`}
                        >
                          <span className="min-w-0">
                            <span className="block truncate text-sm font-black text-black">
                              {entry.title}
                            </span>
                            <span className="mt-0.5 block truncate text-xs font-semibold text-[#817A72]">
                              {entry.time}
                              {calendarCohortId === "all" && cohort
                                ? ` · ${cohort.name}`
                                : ""}
                            </span>
                          </span>
                          <span
                            className={`h-3 w-3 shrink-0 rounded-full ${
                              entry.kind === "assignment"
                                ? "bg-black"
                                : "bg-[#ffd400] ring-1 ring-black"
                            }`}
                          />
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          ) : (
          <div className="w-full overflow-hidden">
            <div className="w-full min-w-0">
              <div className="grid grid-cols-7 border-b border-[#EEEAE4] bg-[#FAF9F7]">
                {["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Нд"].map((day) => (
                  <div
                    key={day}
                    className="px-1 py-2 text-center text-[10px] font-bold uppercase text-[#817A72] sm:px-2 sm:py-3 sm:text-xs"
                  >
                    {day}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7">
                {calendarCells.map((day, index) => {
                  const dayDate = day
                    ? new Date(
                        calendarMonth.getFullYear(),
                        calendarMonth.getMonth(),
                        day
                      )
                    : null;
                  const entries = dayDate
                    ? entriesByDate.get(dateKey(dayDate)) || []
                    : [];
                  const isToday =
                    dayDate &&
                    dateKey(dayDate) === dateKey(new Date());

                  return (
                    <div
                      key={`${day || "empty"}-${index}`}
                      className={`min-h-[82px] min-w-0 border-b border-r border-[#EEEAE4] p-1 sm:min-h-[128px] sm:p-2 ${
                        day ? "bg-white" : "bg-[#FAF9F7]"
                      }`}
                    >
                      {day && (
                        <>
                          <span
                            className={`grid h-7 w-7 place-items-center rounded-lg text-xs font-bold ${
                              isToday
                                ? "border-2 border-black bg-[#ffd400] text-black"
                                : "text-[#514B45]"
                            }`}
                          >
                            {day}
                          </span>
                          <div className="mt-1.5 space-y-1 sm:mt-2 sm:space-y-1.5">
                            {entries.slice(0, 2).map((entry) => {
                              const cohort = cohortById.get(entry.cohortId);
                              return (
                                <button
                                  type="button"
                                  key={`${entry.kind}-${entry.id}`}
                                  title={`${cohort?.name || "Потік"}: ${
                                    entry.title
                                  }`}
                                  onClick={() => {
                                    if (entry.kind === "activity") {
                                      openActivityDetails(entry.id);
                                    }
                                  }}
                                  className={`block w-full min-w-0 rounded-md border-l-2 px-1 py-1 text-left text-[9px] leading-tight transition sm:rounded-lg sm:border-l-4 sm:px-2 sm:py-1.5 sm:text-[11px] ${
                                    entry.kind === "assignment"
                                      ? "border-black bg-[#fff8cf] text-black"
                                      : "border-[#ffd400] bg-white text-black hover:bg-[#fff8cf]"
                                  }`}
                                >
                                  <p className="truncate font-bold">
                                    {entry.title}
                                  </p>
                                  <p className="mt-0.5 hidden truncate opacity-75 sm:block">
                                    {entry.time}
                                    {calendarCohortId === "all" && cohort
                                      ? ` · ${cohort.name}`
                                      : ""}
                                  </p>
                                </button>
                              );
                            })}
                            {entries.length > 2 && (
                              <p className="truncate px-1 text-[9px] font-bold text-[#817A72] sm:text-[11px]">
                                Ще {entries.length - 2}
                              </p>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          )}
          <div className="flex flex-wrap gap-4 border-t border-[#EEEAE4] px-4 py-3 text-xs font-semibold text-[#716A62] sm:px-5">
            <span className="inline-flex items-center gap-2">
              <i className="h-2.5 w-2.5 rounded-full bg-[#ffd400] ring-1 ring-black" />
              Активність
            </span>
            <span className="inline-flex items-center gap-2">
              <i className="h-2.5 w-2.5 rounded-full bg-black" />
              Дедлайн завдання
            </span>
          </div>
        </div>

        <div className="min-w-0">
          {createModalMode && (
            <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/45 px-3 py-4 backdrop-blur-sm sm:items-center">
              <button
                type="button"
                onClick={closeCreateModal}
                className="absolute inset-0"
                aria-label="Закрити форму"
              />
              <div className="relative z-10 flex max-h-[92dvh] w-full max-w-2xl flex-col overflow-hidden rounded-[26px] border-2 border-black bg-white shadow-[8px_8px_0_#ffd400]">
          <form
            onSubmit={createActivity}
            className="flex min-h-0 min-w-0 flex-1 flex-col"
          >
            <div className="sticky top-0 z-20 shrink-0 border-b-2 border-black bg-[#fffdf2] px-4 py-5 sm:px-5">
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border-2 border-black bg-[#ffd400] text-black shadow-[3px_3px_0_#000]">
                  {createModalMode === "consultation" ? (
                    <Users size={20} />
                  ) : (
                    <CalendarPlus size={20} />
                  )}
                </div>
                <div className="min-w-0">
                  <h3 className="text-xl font-black tracking-[-0.03em] text-black">
                    {createModalMode === "consultation"
                      ? "Нова консультація"
                      : "Нова активність"}
                  </h3>
                  <p className="text-sm leading-5 text-[#857E76]">
                    {createModalMode === "consultation"
                      ? "Подія буде видима тільки вибраним учасникам."
                      : "Подія з’явиться у всіх учасників потоку."}
                  </p>
                </div>
                </div>
                <button
                  type="button"
                  onClick={closeCreateModal}
                  className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border-2 border-black bg-white text-black transition hover:bg-[#ffd400]"
                  aria-label="Закрити"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="min-h-0 min-w-0 flex-1 space-y-4 overflow-y-auto p-4 sm:p-5">
              <label className="block min-w-0">
                <span className="text-sm font-semibold text-[#4E4943]">
                  Потік
                </span>
                <select
                  required
                  value={cohortId}
                  onChange={(event) => {
                    setCohortId(event.target.value);
                    setForm({
                      ...form,
                      consultationParticipantId: "",
                      additionalParticipantIds: [],
                    });
                  }}
                  className="mt-2 h-11 w-full min-w-0 rounded-lg border border-[#DCD7CF] px-3"
                >
                  <option value="">Оберіть потік</option>
                  {cohorts.map((cohort) => (
                    <option key={cohort._id} value={cohort._id}>
                      {cohort.name} · {cohort.code}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block min-w-0">
                <span className="text-sm font-semibold text-[#4E4943]">
                  Назва
                </span>
                <input
                  required
                  value={form.title}
                  onChange={(event) =>
                    setForm({ ...form, title: event.target.value })
                  }
                  className="mt-2 h-11 w-full min-w-0 rounded-lg border border-[#DCD7CF] px-3"
                />
              </label>
              {createModalMode === "activity" ? (
                <label className="block min-w-0">
                  <span className="text-sm font-semibold text-[#4E4943]">
                    Тип активності
                  </span>
                  <select
                    value={form.type}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        type: event.target.value as CalendarActivity["type"],
                        consultationParticipantId: "",
                        includeAdditionalParticipants: false,
                        additionalParticipantIds: [],
                      })
                    }
                    className="mt-2 h-11 w-full min-w-0 rounded-lg border border-[#DCD7CF] px-3"
                  >
                    <option value="offline">Офлайн</option>
                    <option value="online">Онлайн</option>
                    <option value="self_study">Самостійна робота</option>
                    <option value="community">Спільнота</option>
                  </select>
                </label>
              ) : (
                <div className="rounded-2xl border border-black/10 bg-[#fff8cf] px-4 py-3">
                  <span className="text-xs font-black uppercase tracking-[0.12em] text-[#8a6f00]">
                    Тип події
                  </span>
                  <p className="mt-1 text-base font-black text-black">
                    Персональна консультація
                  </p>
                </div>
              )}
              {form.type === "consultation" && (
                <div className="rounded-2xl border-2 border-black bg-[#fffdf2] p-4 shadow-[4px_4px_0_#ffd400]">
                  <div className="mb-3 flex items-start gap-3">
                    <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#ffd400] text-black">
                      <Users size={18} />
                    </div>
                    <div className="min-w-0">
                      <p className="font-black text-black">
                        Учасники консультації
                      </p>
                      <p className="mt-1 text-xs font-semibold leading-5 text-[#817A72]">
                        За замовчуванням консультація створюється для однієї
                        людини. Можна додати ще учасників.
                      </p>
                    </div>
                  </div>

                  <label className="block min-w-0">
                    <span className="text-sm font-semibold text-[#4E4943]">
                      Основний учасник
                    </span>
                    <select
                      required
                      value={form.consultationParticipantId}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          consultationParticipantId: event.target.value,
                          additionalParticipantIds:
                            form.additionalParticipantIds.filter(
                              (id) => id !== event.target.value
                            ),
                        })
                      }
                      className="mt-2 h-11 w-full min-w-0 rounded-lg border border-[#DCD7CF] bg-white px-3"
                    >
                      <option value="">Оберіть учасника</option>
                      {selectedFormParticipants.map((participant) => (
                        <option key={participant._id} value={participant._id}>
                          {participant.username}
                          {participant.telephone
                            ? ` · ${participant.telephone}`
                            : ""}
                        </option>
                      ))}
                    </select>
                  </label>

                  {selectedFormParticipants.length === 0 && (
                    <p className="mt-3 rounded-xl bg-white px-3 py-2 text-xs font-bold text-[#8A4A2E]">
                      У цьому потоці поки немає учасників.
                    </p>
                  )}

                  <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-xl border border-black/10 bg-white px-3 py-3">
                    <input
                      type="checkbox"
                      checked={form.includeAdditionalParticipants}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          includeAdditionalParticipants: event.target.checked,
                          additionalParticipantIds: event.target.checked
                            ? form.additionalParticipantIds
                            : [],
                        })
                      }
                      className="mt-1 h-4 w-4 accent-[#ffd400]"
                    />
                    <span>
                      <span className="block text-sm font-black text-black">
                        Додати інших учасників
                      </span>
                      <span className="mt-0.5 block text-xs font-semibold text-[#817A72]">
                        Консультацію побачать усі вибрані люди.
                      </span>
                    </span>
                  </label>

                  {form.includeAdditionalParticipants && (
                    <div className="mt-3 max-h-56 space-y-2 overflow-y-auto pr-1">
                      {selectedFormParticipants
                        .filter(
                          (participant) =>
                            participant._id !== form.consultationParticipantId
                        )
                        .map((participant) => {
                          const checked =
                            form.additionalParticipantIds.includes(
                              participant._id
                            );

                          return (
                            <label
                              key={participant._id}
                              className={`flex cursor-pointer items-center gap-3 rounded-xl border px-3 py-2 transition ${
                                checked
                                  ? "border-black bg-[#ffd400]"
                                  : "border-black/10 bg-white hover:border-black/30"
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => {
                                  const nextAdditional = checked
                                    ? form.additionalParticipantIds.filter(
                                        (id) => id !== participant._id
                                      )
                                    : [
                                        ...form.additionalParticipantIds,
                                        participant._id,
                                      ];

                                  setForm({
                                    ...form,
                                    additionalParticipantIds: nextAdditional,
                                  });
                                }}
                                className="h-4 w-4 accent-black"
                              />
                              <span className="min-w-0">
                                <span className="block truncate text-sm font-black text-black">
                                  {participant.username}
                                </span>
                                <span className="block truncate text-xs font-semibold text-black/60">
                                  {participant.telephone}
                                  {participant.email
                                    ? ` · ${participant.email}`
                                    : ""}
                                </span>
                              </span>
                            </label>
                          );
                        })}
                    </div>
                  )}
                </div>
              )}
              <div className="grid min-w-0 gap-3">
                <label className="min-w-0">
                  <span className="text-sm font-semibold text-[#4E4943]">
                    Початок
                  </span>
                  <input
                    required
                    type="datetime-local"
                    step={DATETIME_STEP_SECONDS}
                    value={form.startsAt}
                    onChange={(event) =>
                      setForm({ ...form, startsAt: event.target.value })
                    }
                    className="mt-2 h-11 w-full min-w-0 rounded-lg border border-[#DCD7CF] px-2 text-sm"
                  />
                </label>
                <label className="min-w-0">
                  <span className="text-sm font-semibold text-[#4E4943]">
                    Завершення
                  </span>
                  <input
                    required
                    type="datetime-local"
                    step={DATETIME_STEP_SECONDS}
                    value={form.endsAt}
                    onChange={(event) =>
                      setForm({ ...form, endsAt: event.target.value })
                    }
                    className="mt-2 h-11 w-full min-w-0 rounded-lg border border-[#DCD7CF] px-2 text-sm"
                  />
                </label>
              </div>
              <label className="block min-w-0">
                <span className="text-sm font-semibold text-[#4E4943]">
                  Місце
                </span>
                <input
                  value={form.location}
                  onChange={(event) =>
                    setForm({ ...form, location: event.target.value })
                  }
                  className="mt-2 h-11 w-full min-w-0 rounded-lg border border-[#DCD7CF] px-3"
                />
              </label>
              <label className="block min-w-0">
                <span className="text-sm font-semibold text-[#4E4943]">
                  Посилання на зустріч
                </span>
                <input
                  type="url"
                  value={form.meetingUrl}
                  onChange={(event) =>
                    setForm({ ...form, meetingUrl: event.target.value })
                  }
                  className="mt-2 h-11 w-full min-w-0 rounded-lg border border-[#DCD7CF] px-3"
                />
              </label>
              <label className="block min-w-0">
                <span className="text-sm font-semibold text-[#4E4943]">
                  Опис
                </span>
                <textarea
                  rows={4}
                  value={form.description}
                  onChange={(event) =>
                    setForm({ ...form, description: event.target.value })
                  }
                  className="mt-2 w-full min-w-0 rounded-lg border border-[#DCD7CF] px-3 py-2"
                />
              </label>

              <div className="rounded-2xl border border-[#E3DED6] bg-[#fffdf7] p-4">
                <p className="text-sm font-black text-black">Матеріали</p>
                <p className="mt-1 text-xs font-semibold text-[#817A72]">
                  Можна додати файли, які побачать учасники.
                </p>
                <div className="mt-3">
                  <MaterialDropzone
                    files={createMaterialFiles}
                    onFilesChange={setCreateMaterialFiles}
                  />
                </div>
              </div>
              <button
                disabled={saving}
                className="inline-flex h-11 w-full min-w-0 items-center justify-center gap-2 rounded-2xl border-2 border-black bg-[#ffd400] px-3 text-xs font-black text-black shadow-[4px_4px_0_#000] disabled:opacity-60 sm:text-sm"
              >
                <CalendarPlus size={17} className="shrink-0" />
                {saving
                  ? "Створюємо..."
                  : createModalMode === "consultation"
                  ? "Створити консультацію"
                  : "Додати активність"}
              </button>
            </div>
          </form>
              </div>
            </div>
          )}

          <div className="min-w-0">
            {loading ? (
              <div className="rounded-xl border border-[#E3DED6] bg-white p-8">
                Завантажуємо...
              </div>
            ) : activities.length === 0 ? (
              <div className="rounded-xl border border-dashed border-[#D8D2C9] bg-white p-10 text-center text-[#817A72]">
                У цьому потоці ще немає активностей.
              </div>
            ) : (
              <div className="space-y-3">
                {activities.map((activity) => {
                  const participantNames = activityParticipantNames(activity);

                  return (
                    <article
                      key={activity._id}
                      onClick={() => openActivityDetails(activity._id)}
                      className="min-w-0 cursor-pointer overflow-hidden rounded-xl border border-[#E3DED6] bg-white p-4 transition hover:border-black hover:shadow-[4px_4px_0_#ffd400] sm:p-5"
                    >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap gap-2">
                          <span className="rounded-full border border-black/10 bg-[#fff8cf] px-3 py-1 text-xs font-black text-black">
                            {activityTypeLabels[activity.type]}
                          </span>
                          {activity.audience === "selected" && (
                            <span className="rounded-full border border-black/10 bg-black px-3 py-1 text-xs font-black text-[#ffd400]">
                              {participantNames.length
                                ? participantNames.length === 1
                                  ? participantNames[0]
                                  : `${participantNames.length} учасники`
                                : "Вибрані учасники"}
                            </span>
                          )}
                        </div>
                        <h3 className="mt-3 break-words text-lg font-bold text-[#312D28]">
                          {activity.title}
                        </h3>
                        <p className="mt-2 break-words text-sm leading-6 text-[#777068] [overflow-wrap:anywhere]">
                          {activity.description}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          removeActivity(activity._id);
                        }}
                        className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-[#9D3D2E] hover:bg-[#FFF0ED]"
                        aria-label="Видалити активність"
                      >
                        <Trash2 size={17} />
                      </button>
                    </div>
                    <div className="mt-4 grid min-w-0 gap-2 border-t border-[#EEEAE4] pt-4 text-sm text-[#625C55] sm:flex sm:flex-wrap sm:items-center sm:gap-4">
                      <span className="inline-flex min-w-0 items-center gap-2">
                        <Clock3 size={16} className="shrink-0 text-[#8a6f00]" />
                        <span className="min-w-0 truncate">
                          {new Date(activity.startsAt).toLocaleString("uk-UA")}
                        </span>
                      </span>
                      {activity.location && (
                        <span className="inline-flex min-w-0 items-center gap-2">
                          <MapPin size={16} className="shrink-0" />
                          <span className="min-w-0 truncate">
                            {activity.location}
                          </span>
                        </span>
                      )}
                      {activity.meetingUrl && (
                        <a
                          href={activity.meetingUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex min-w-0 items-center gap-2 font-bold text-black hover:text-[#8a6f00]"
                        >
                          <ExternalLink size={16} className="shrink-0" />
                          <span className="min-w-0 truncate">Посилання</span>
                        </a>
                      )}
                    </div>
                  </article>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        </>
        )}

        {(selectedActivityDetails || activityDetailsLoading) && (
          <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/45 px-3 py-4 backdrop-blur-sm sm:items-center">
            <div className="max-h-[92dvh] w-full max-w-6xl overflow-hidden rounded-[26px] border-2 border-black bg-white shadow-[8px_8px_0_#ffd400]">
              <div className="flex items-start justify-between gap-4 border-b-2 border-black bg-[#fffdf2] px-4 py-4 sm:px-6">
                <div className="min-w-0">
                  <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#8a6f00]">
                    Деталі активності
                  </p>
                  <h3 className="mt-1 truncate text-2xl font-black tracking-[-0.04em] text-black">
                    {selectedActivityDetails?.activity.title ||
                      "Завантажуємо активність..."}
                  </h3>
                  {selectedActivityDetails?.cohort && (
                    <p className="mt-1 text-sm font-semibold text-[#625C55]">
                      {selectedActivityDetails.cohort.name} ·{" "}
                      {selectedActivityDetails.cohort.code}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={closeActivityDetails}
                  className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl border-2 border-black bg-[#ffd400] text-black shadow-[3px_3px_0_#000]"
                  aria-label="Закрити"
                >
                  <X size={20} />
                </button>
              </div>

              {activityDetailsLoading && !selectedActivityDetails ? (
                <div className="p-8 text-center text-sm font-bold text-[#625C55]">
                  Завантажуємо деталі...
                </div>
              ) : selectedActivityDetails ? (
                <div className="grid max-h-[calc(92dvh-98px)] overflow-y-auto lg:grid-cols-[1fr_0.9fr]">
                  <form
                    onSubmit={saveActivityDetails}
                    className="border-b border-[#EEEAE4] p-4 sm:p-6 lg:border-b-0 lg:border-r"
                  >
                    <div className="mb-5 flex items-center gap-3">
                      <div className="grid h-11 w-11 place-items-center rounded-2xl bg-[#fff8cf] text-black">
                        <CalendarDays size={21} />
                      </div>
                      <div>
                        <h4 className="text-lg font-black text-black">
                          Інформація про активність
                        </h4>
                        <p className="text-sm font-semibold text-[#817A72]">
                          Тут можна поправити назву, час, місце і статус.
                        </p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <label className="block">
                        <span className="text-sm font-black text-[#4E4943]">
                          Назва
                        </span>
                        <input
                          required
                          value={activityEditForm.title}
                          onChange={(event) =>
                            setActivityEditForm({
                              ...activityEditForm,
                              title: event.target.value,
                            })
                          }
                          className="mt-2 h-11 w-full rounded-xl border border-[#DCD7CF] px-3 font-semibold"
                        />
                      </label>

                      <div className="grid gap-3 sm:grid-cols-2">
                        <label>
                          <span className="text-sm font-black text-[#4E4943]">
                            Тип
                          </span>
                          <select
                            value={activityEditForm.type}
                            onChange={(event) =>
                              setActivityEditForm({
                                ...activityEditForm,
                                type: event.target
                                  .value as CalendarActivity["type"],
                              })
                            }
                            className="mt-2 h-11 w-full rounded-xl border border-[#DCD7CF] px-3 font-semibold"
                          >
                            {Object.entries(activityTypeLabels).map(
                              ([value, label]) => (
                                <option key={value} value={value}>
                                  {label}
                                </option>
                              )
                            )}
                          </select>
                        </label>
                        <label>
                          <span className="text-sm font-black text-[#4E4943]">
                            Статус
                          </span>
                          <select
                            value={activityEditForm.status}
                            onChange={(event) =>
                              setActivityEditForm({
                                ...activityEditForm,
                                status: event.target
                                  .value as CalendarActivity["status"],
                              })
                            }
                            className="mt-2 h-11 w-full rounded-xl border border-[#DCD7CF] px-3 font-semibold"
                          >
                            {Object.entries(activityStatusLabels).map(
                              ([value, label]) => (
                                <option key={value} value={value}>
                                  {label}
                                </option>
                              )
                            )}
                          </select>
                        </label>
                      </div>

                      <div className="grid gap-3 sm:grid-cols-2">
                        <label>
                          <span className="text-sm font-black text-[#4E4943]">
                            Початок
                          </span>
                          <input
                            required
                            type="datetime-local"
                            step={DATETIME_STEP_SECONDS}
                            value={activityEditForm.startsAt}
                            onChange={(event) =>
                              setActivityEditForm({
                                ...activityEditForm,
                                startsAt: event.target.value,
                              })
                            }
                            className="mt-2 h-11 w-full rounded-xl border border-[#DCD7CF] px-3 text-sm font-semibold"
                          />
                        </label>
                        <label>
                          <span className="text-sm font-black text-[#4E4943]">
                            Завершення
                          </span>
                          <input
                            required
                            type="datetime-local"
                            step={DATETIME_STEP_SECONDS}
                            value={activityEditForm.endsAt}
                            onChange={(event) =>
                              setActivityEditForm({
                                ...activityEditForm,
                                endsAt: event.target.value,
                              })
                            }
                            className="mt-2 h-11 w-full rounded-xl border border-[#DCD7CF] px-3 text-sm font-semibold"
                          />
                        </label>
                      </div>

                      <label className="block">
                        <span className="text-sm font-black text-[#4E4943]">
                          Місце
                        </span>
                        <input
                          value={activityEditForm.location}
                          onChange={(event) =>
                            setActivityEditForm({
                              ...activityEditForm,
                              location: event.target.value,
                            })
                          }
                          className="mt-2 h-11 w-full rounded-xl border border-[#DCD7CF] px-3 font-semibold"
                        />
                      </label>

                      <label className="block">
                        <span className="text-sm font-black text-[#4E4943]">
                          Посилання на зустріч
                        </span>
                        <input
                          type="url"
                          value={activityEditForm.meetingUrl}
                          onChange={(event) =>
                            setActivityEditForm({
                              ...activityEditForm,
                              meetingUrl: event.target.value,
                            })
                          }
                          className="mt-2 h-11 w-full rounded-xl border border-[#DCD7CF] px-3 font-semibold"
                        />
                      </label>

                      <label className="block">
                        <span className="text-sm font-black text-[#4E4943]">
                          Опис
                        </span>
                        <textarea
                          rows={4}
                          value={activityEditForm.description}
                          onChange={(event) =>
                            setActivityEditForm({
                              ...activityEditForm,
                              description: event.target.value,
                            })
                          }
                          className="mt-2 w-full rounded-xl border border-[#DCD7CF] px-3 py-2 font-semibold"
                        />
                      </label>

                      <div className="space-y-3 rounded-2xl border border-[#E3DED6] bg-[#fffdf7] p-4">
                        <ProgramMaterials
                          materials={selectedActivityDetails.activity.materials}
                          title="Прикріплені файли активності"
                          emptyText="До цієї активності ще не прикріплено жодного файлу."
                          onDelete={deleteDetailsMaterial}
                        />
                        <div>
                          <p className="text-sm font-black text-black">
                            Додати матеріали
                          </p>
                          <p className="mt-1 text-xs font-semibold text-[#817A72]">
                            До 5 файлів за раз, максимум 25 MB кожен.
                          </p>
                        </div>
                        <MaterialDropzone
                          files={detailsMaterialFiles}
                          onFilesChange={setDetailsMaterialFiles}
                        />
                        <button
                          type="button"
                          onClick={addDetailsMaterials}
                          disabled={materialsSaving}
                          className="inline-flex h-10 w-full items-center justify-center rounded-xl border-2 border-black bg-white px-4 text-xs font-black text-black shadow-[3px_3px_0_#ffd400] disabled:opacity-60 sm:w-auto"
                        >
                          {materialsSaving ? "Зберігаємо..." : "Додати матеріали"}
                        </button>
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={activityDetailsSaving}
                      className="mt-5 inline-flex h-12 w-full items-center justify-center gap-2 rounded-2xl border-2 border-black bg-[#ffd400] text-sm font-black text-black shadow-[4px_4px_0_#000] disabled:opacity-60 sm:w-auto sm:px-6"
                    >
                      <Save size={17} />
                      {activityDetailsSaving
                        ? "Зберігаємо..."
                        : "Зберегти зміни"}
                    </button>
                  </form>

                  <div className="p-4 sm:p-6">
                    <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="flex items-center gap-3">
                        <div className="grid h-11 w-11 place-items-center rounded-2xl bg-[#fff8cf] text-black">
                          <Users size={21} />
                        </div>
                        <div>
                          <h4 className="text-lg font-black text-black">
                            Відвідуваність
                          </h4>
                          <p className="text-sm font-semibold text-[#817A72]">
                            Познач присутніх учасників потоку.
                          </p>
                        </div>
                      </div>
                      <div className="rounded-2xl border border-black/10 bg-[#f7f8fb] px-4 py-2 text-sm font-black text-black">
                        {selectedPresentIds.size}/
                        {selectedActivityDetails.participants.length}
                      </div>
                    </div>

                    <div className="mb-4 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          setSelectedPresentIds(
                            new Set(
                              selectedActivityDetails.participants.map(
                                (participant) => participant.user._id
                              )
                            )
                          )
                        }
                        className="rounded-full border border-black/10 bg-[#fff8cf] px-4 py-2 text-xs font-black text-black hover:border-black"
                      >
                        Позначити всіх
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedPresentIds(new Set())}
                        className="rounded-full border border-black/10 bg-white px-4 py-2 text-xs font-black text-[#625C55] hover:border-black"
                      >
                        Очистити
                      </button>
                    </div>

                    <div className="max-h-[440px] space-y-2 overflow-y-auto pr-1">
                      {selectedActivityDetails.participants.length === 0 ? (
                        <div className="rounded-2xl border border-dashed border-[#D8D2C9] p-6 text-center text-sm font-semibold text-[#817A72]">
                          У цьому потоці поки немає учасників.
                        </div>
                      ) : (
                        selectedActivityDetails.participants.map(
                          (participant) => {
                            const checked = selectedPresentIds.has(
                              participant.user._id
                            );

                            return (
                              <label
                                key={participant.user._id}
                                className={`flex cursor-pointer items-center gap-3 rounded-2xl border px-4 py-3 transition ${
                                  checked
                                    ? "border-black bg-[#fff8cf]"
                                    : "border-[#E3DED6] bg-white hover:border-black/30"
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() =>
                                    toggleParticipantAttendance(
                                      participant.user._id
                                    )
                                  }
                                  className="h-5 w-5 accent-[#ffd400]"
                                />
                                <div className="min-w-0 flex-1">
                                  <p className="truncate text-sm font-black text-black">
                                    {participant.user.username}
                                  </p>
                                  <p className="truncate text-xs font-semibold text-[#817A72]">
                                    {participant.user.telephone}
                                    {participant.user.email
                                      ? ` · ${participant.user.email}`
                                      : ""}
                                  </p>
                                </div>
                                <span
                                  className={`rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-[0.1em] ${
                                    checked
                                      ? "bg-black text-[#ffd400]"
                                      : "bg-[#f1f3f7] text-[#6b7280]"
                                  }`}
                                >
                                  {checked ? "присутній" : "немає"}
                                </span>
                              </label>
                            );
                          }
                        )
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={saveAttendance}
                      disabled={attendanceSaving}
                      className="mt-5 inline-flex h-12 w-full items-center justify-center gap-2 rounded-2xl border-2 border-black bg-[#ffd400] text-sm font-black text-black shadow-[4px_4px_0_#000] disabled:opacity-60"
                    >
                      <Save size={17} />
                      {attendanceSaving
                        ? "Зберігаємо..."
                        : "Зберегти відвідуваність"}
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default AdminCalendarPage;
